import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CalendarDays, Search, Phone, Users as UsersIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState, StatusBadge, Section } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUSES = ["pending", "confirmed", "seated", "cancelled", "no-show"];

export default function Reservations() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [tables, setTables] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", party_size: 2,
    table_id: "", date: today, time: "19:00", status: "pending", notes: "",
  });

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/reservations", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/tables"),
      ]);
      setList(r1.data);
      setTables(r2.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const tableById = tables.reduce((m, t) => ({ ...m, [t.id]: t }), {});

  const filtered = list
    .filter((r) => filterStatus === "all" ? true : r.status === filterStatus)
    .filter((r) => !search ? true :
      r.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      (r.customer_phone || "").includes(search));

  const save = async () => {
    if (!form.customer_name) { toast.error(t("error")); return; }
    try {
      const payload = { ...form, party_size: Number(form.party_size), table_id: form.table_id || null };
      if (edit) await api.put(`/reservations/${edit.id}`, payload);
      else await api.post("/reservations", payload);
      toast.success(t("success"));
      setOpen(false); setEdit(null);
      setForm({ customer_name: "", customer_phone: "", party_size: 2, table_id: "", date: today, time: "19:00", status: "pending", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/reservations/${id}/status`, { status });
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("reservations")}</h1>
          <p className="text-sm text-gray-500 mt-1">{list.length} {lang === "pashto" ? "ریزرویشن" : "رزرو"}</p>
        </div>
        <button
          data-testid="add-reservation"
          onClick={() => { setEdit(null); setForm({ customer_name: "", customer_phone: "", party_size: 2, table_id: "", date: today, time: "19:00", status: "pending", notes: "" }); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
        >
          <Plus className="w-4 h-4" /> {t("addReservation")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
        </div>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" />
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setFilterStatus("all")} className={`px-3 py-1.5 rounded-full text-sm border ${filterStatus === "all" ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}>{t("allCategories")}</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-full text-sm border ${filterStatus === s ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}>
              {s === "no-show" ? t("noShow") : t(s)}
            </button>
          ))}
        </div>
      </div>

      <Section title={t("reservations")}>
        {filtered.length === 0 ? <EmptyState icon={CalendarDays} title={t("empty")} /> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-right text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="p-3">{t("name")}</th>
                  <th className="p-3">{t("phone")}</th>
                  <th className="p-3">{t("partySize")}</th>
                  <th className="p-3">{t("date")}</th>
                  <th className="p-3">{t("time")}</th>
                  <th className="p-3">{t("table")}</th>
                  <th className="p-3">{t("status")}</th>
                  <th className="p-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="p-3 text-gray-900 font-medium">{r.customer_name}</td>
                    <td className="p-3 text-gray-500 tabular flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer_phone || "-"}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 text-gray-700"><UsersIcon className="w-3.5 h-3.5" />{r.party_size}</span></td>
                    <td className="p-3 text-gray-700 tabular">{r.date}</td>
                    <td className="p-3 text-gray-700 tabular font-mono">{r.time}</td>
                    <td className="p-3 text-gray-700">{tableById[r.table_id] ? `${t("table")} ${tableById[r.table_id].number}` : "-"}</td>
                    <td className="p-3">
                      <StatusBadge status={r.status === "no-show" ? "overdue" : r.status} label={r.status === "no-show" ? t("noShow") : t(r.status)} />
                    </td>
                    <td className="p-3 flex gap-1">
                      {r.status === "pending" && (
                        <button onClick={() => changeStatus(r.id, "confirmed")} className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs hover:bg-emerald-100" data-testid={`res-confirm-${r.id}`}>{t("confirmed")}</button>
                      )}
                      {r.status === "confirmed" && (
                        <button onClick={() => changeStatus(r.id, "seated")} className="px-2 py-1 rounded-md bg-sky-50 text-sky-700 text-xs hover:bg-sky-100">{t("seated")}</button>
                      )}
                      {["pending", "confirmed"].includes(r.status) && (
                        <button onClick={() => changeStatus(r.id, "cancelled")} className="px-2 py-1 rounded-md bg-rose-50 text-rose-700 text-xs hover:bg-rose-100">{t("cancelled")}</button>
                      )}
                      <button onClick={() => { setEdit(r); setForm({ customer_name: r.customer_name, customer_phone: r.customer_phone, party_size: r.party_size, table_id: r.table_id || "", date: r.date, time: r.time, status: r.status, notes: r.notes || "" }); setOpen(true); }} className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(r.id)} className="p-1.5 rounded-md border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{edit ? t("edit") : t("addReservation")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="res-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder={t("customerName")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder={t("phone")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" min={1} value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} placeholder={t("partySize")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
              <RpSelect
                allowEmpty
                value={form.table_id}
                onValueChange={(v) => setForm({ ...form, table_id: v })}
                placeholder={t("selectTable")}
                options={tables.map((tb) => ({
                  value: tb.id,
                  label: `${t("table")} ${tb.number} — ${tb.section}`,
                }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </div>
            <RpSelect
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
              options={STATUSES.map((s) => ({
                value: s,
                label: s === "no-show" ? t("noShow") : t(s),
              }))}
            />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("notes")} className="w-full p-3 rounded-xl border border-gray-200 min-h-[80px]" />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button data-testid="save-reservation" onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
